/**
 * legalMarkdown — a tiny, pure parser for the legal-document markdown subset
 * (docs/plans/consent-capture.md §3.1). It returns plain data (blocks and
 * inline tokens) that the page turns into React elements. Nothing here ever
 * produces HTML strings, so raw HTML in the source is shown as literal text.
 *
 * Supported:
 *   # / ## / ### headings, paragraphs, blank-line separation
 *   "-" or "*" unordered lists and "1." ordered lists (one level)
 *   **bold**, *italic*, [text](url) with https:, mailto: or site-relative "/…" urls
 *   --- horizontal rule
 *
 * Block:  { type: 'heading', level: 1|2|3, children: Inline[] }
 *         { type: 'paragraph', children: Inline[] }
 *         { type: 'list', ordered: boolean, start: number, items: Inline[][] }
 *         { type: 'hr' }
 * Inline: { type: 'text', text } | { type: 'strong', children } |
 *         { type: 'em', children } | { type: 'link', href, children }
 *
 * Performance: parsing is linear in the input size, so hostile or broken text
 * (a 20k-character line of "#", 80k characters of "[x](", long runs of spaces or
 * unclosed "*") cannot freeze the page. No backtracking regex runs on unbounded
 * input; closing-delimiter searches are memoized because the scan position only
 * moves forward, and link urls use a precomputed parenthesis match.
 */

const ESCAPABLE = new Set(['\\', '*', '_', '[', ']', '(', ')', '#', '-', '+', '.', '!', '`']);

/** Returns the url when it is allowed as a link target, otherwise null. */
export const safeLinkHref = (rawUrl) => {
  if (typeof rawUrl !== 'string') return null;
  const url = rawUrl.trim();
  if (!url || url.length > 2048) return null;
  // Control characters, whitespace or backslashes can smuggle a scheme past
  // naive checks ("java\tscript:"); refuse them outright.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000- \u007f\\]/.test(url)) return null;
  const lower = url.toLowerCase();
  if (lower.startsWith('https://') && url.length > 'https://'.length) return url;
  if (lower.startsWith('mailto:') && url.length > 'mailto:'.length) return url;
  // Site-relative only: "/path" but never protocol-relative "//host".
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  return null;
};

const pushText = (tokens, text) => {
  if (!text) return;
  const last = tokens[tokens.length - 1];
  if (last && last.type === 'text') {
    last.text += text;
  } else {
    tokens.push({ type: 'text', text });
  }
};

/**
 * For every "(" the index of its matching ")" within the same whitespace-free run,
 * or -1. One pass with a stack, so link-url lookup is O(1) per "[" instead of a
 * rescan (same result as scanning forward with a depth counter until a space).
 */
const buildParenMatches = (source) => {
  const match = new Int32Array(source.length).fill(-1);
  const stack = [];
  for (let i = 0; i < source.length; i += 1) {
    const ch = source[i];
    if (ch === ' ' || ch === '\t' || ch === '\n') stack.length = 0;
    else if (ch === '(') stack.push(i);
    else if (ch === ')' && stack.length) match[stack.pop()] = i;
  }
  return match;
};

/**
 * Wraps a forward search `scan(from) -> index | -1` with a memo valid for
 * non-decreasing `from`: when the previous answer is still ahead of `from`, no
 * closer exists in between (the earlier scan already passed that stretch), so it
 * is reused. Total scanning per parseInline call is therefore O(n).
 */
const memoForward = (scan) => {
  let lastFrom = -1;
  let lastResult = -1;
  return (from) => {
    if (lastFrom !== -1 && from >= lastFrom && (lastResult === -1 || lastResult >= from)) {
      return lastResult;
    }
    lastFrom = from;
    lastResult = scan(from);
    return lastResult;
  };
};

/** Finds a closing single "*" for italics that is not part of a "**" pair. */
const findEmphasisEnd = (source, start) => {
  for (let i = start; i < source.length; i += 1) {
    if (source[i] === '\\') {
      i += 1;
    } else if (source[i] === '*') {
      if (source[i + 1] === '*') {
        i += 1; // skip a nested "**" pair marker
      } else {
        return i;
      }
    }
  }
  return -1;
};

const findStrongEnd = (source, start) => {
  for (let i = start; i < source.length - 1; i += 1) {
    if (source[i] === '\\') {
      i += 1;
    } else if (source[i] === '*' && source[i + 1] === '*') {
      return i;
    }
  }
  return -1;
};

/** Parses inline markdown into tokens. `allowLinks` is false inside link text. */
export const parseInline = (source, { allowLinks = true } = {}) => {
  const tokens = [];
  const text = String(source || '');
  let i = 0;
  const nextStrongEnd = memoForward((from) => findStrongEnd(text, from));
  const nextEmphasisEnd = memoForward((from) => findEmphasisEnd(text, from));
  const nextLabelEnd = memoForward((from) => text.indexOf(']', from));
  let parenMatches = null; // built on the first link candidate only

  while (i < text.length) {
    const ch = text[i];

    if (ch === '\\' && i + 1 < text.length && ESCAPABLE.has(text[i + 1])) {
      pushText(tokens, text[i + 1]);
      i += 2;
      continue;
    }

    if (ch === '*' && text[i + 1] === '*') {
      const end = nextStrongEnd(i + 2);
      const inner = end > -1 ? text.slice(i + 2, end) : '';
      if (end > -1 && inner.trim() && inner === inner.trim()) {
        tokens.push({ type: 'strong', children: parseInline(inner, { allowLinks }) });
        i = end + 2;
        continue;
      }
      pushText(tokens, '**');
      i += 2;
      continue;
    }

    if (ch === '*') {
      const end = nextEmphasisEnd(i + 1);
      const inner = end > -1 ? text.slice(i + 1, end) : '';
      if (end > -1 && inner.trim() && inner === inner.trim()) {
        tokens.push({ type: 'em', children: parseInline(inner, { allowLinks }) });
        i = end + 1;
        continue;
      }
      pushText(tokens, '*');
      i += 1;
      continue;
    }

    if (ch === '[' && allowLinks) {
      const labelEnd = nextLabelEnd(i + 1);
      if (labelEnd > i + 1 && text[labelEnd + 1] === '(') {
        if (!parenMatches) parenMatches = buildParenMatches(text);
        const urlEnd = parenMatches[labelEnd + 1];
        if (urlEnd > -1) {
          const label = text.slice(i + 1, labelEnd);
          const url = text.slice(labelEnd + 2, urlEnd);
          const href = safeLinkHref(url);
          if (href && !label.includes('[')) {
            tokens.push({
              type: 'link',
              href,
              children: parseInline(label, { allowLinks: false }),
            });
          } else {
            // Disallowed scheme (javascript:, data:, http:, …): show the
            // source literally so nothing is hidden and nothing is clickable.
            pushText(tokens, text.slice(i, urlEnd + 1));
          }
          i = urlEnd + 1;
          continue;
        }
      }
    }

    pushText(tokens, ch);
    i += 1;
  }

  return tokens;
};

const HEADING_PREFIX_RE = /^(#{1,3})[ \t]+/;
const HR_RE = /^[ \t]{0,3}(?:-[ \t]*){3,}$|^[ \t]{0,3}(?:\*[ \t]*){3,}$|^[ \t]{0,3}(?:_[ \t]*){3,}$/;
const UL_RE = /^[ \t]{0,3}[-*][ \t]+(.*)$/;
const OL_RE = /^[ \t]{0,3}(\d{1,9})[.)][ \t]+(.*)$/;

const isSpaceOrTab = (ch) => ch === ' ' || ch === '\t';

/** Removes trailing spaces/tabs (a loop: /[ \t]+$/ backtracks quadratically). */
const trimTrailingBlanks = (line) => {
  let end = line.length;
  while (end > 0 && isSpaceOrTab(line[end - 1])) end -= 1;
  return end === line.length ? line : line.slice(0, end);
};

/**
 * "# Title", "## Title ##" -> { level, text } or null. Linear equivalent of the old
 * /^(#{1,3})[ \t]+(.+?)[ \t]*#*[ \t]*$/ (an optional closing run of "#" and blanks
 * is dropped; the text keeps at least one character).
 */
const parseHeading = (line) => {
  const prefix = line.match(HEADING_PREFIX_RE);
  if (!prefix) return null;
  const rest = line.slice(prefix[0].length);
  if (!rest) return null;
  let end = rest.length;
  while (end > 1 && isSpaceOrTab(rest[end - 1])) end -= 1;
  while (end > 1 && rest[end - 1] === '#') end -= 1;
  while (end > 1 && isSpaceOrTab(rest[end - 1])) end -= 1;
  return { level: prefix[1].length, text: rest.slice(0, end) };
};

/** Parses a legal markdown document into a flat list of blocks. */
export const parseLegalMarkdown = (source) => {
  const lines = String(source || '')
    .replace(/\r\n?/g, '\n')
    .split('\n');

  const blocks = [];
  let paragraph = null; // string[] of lines
  let list = null; // { ordered, start, items: string[] }

  const flushParagraph = () => {
    if (paragraph && paragraph.length) {
      blocks.push({ type: 'paragraph', children: parseInline(paragraph.join(' ')) });
    }
    paragraph = null;
  };
  const flushList = () => {
    if (list && list.items.length) {
      blocks.push({
        type: 'list',
        ordered: list.ordered,
        start: list.start,
        items: list.items.map((item) => parseInline(item)),
      });
    }
    list = null;
  };
  const flushAll = () => {
    flushParagraph();
    flushList();
  };

  for (const rawLine of lines) {
    const line = trimTrailingBlanks(rawLine);

    if (!line.trim()) {
      flushAll();
      continue;
    }

    const heading = parseHeading(line);
    if (heading) {
      flushAll();
      blocks.push({
        type: 'heading',
        level: heading.level,
        children: parseInline(heading.text),
      });
      continue;
    }

    if (HR_RE.test(line)) {
      flushAll();
      blocks.push({ type: 'hr' });
      continue;
    }

    const unordered = line.match(UL_RE);
    const ordered = unordered ? null : line.match(OL_RE);
    if (unordered || ordered) {
      flushParagraph();
      const isOrdered = Boolean(ordered);
      if (!list || list.ordered !== isOrdered) {
        flushList();
        list = {
          ordered: isOrdered,
          start: isOrdered ? Number.parseInt(ordered[1], 10) : 1,
          items: [],
        };
      }
      list.items.push((isOrdered ? ordered[2] : unordered[1]).trim());
      continue;
    }

    // An indented line directly under a list item continues that item.
    if (list && /^[ \t]+\S/.test(line)) {
      const lastIndex = list.items.length - 1;
      list.items[lastIndex] = `${list.items[lastIndex]} ${line.trim()}`.trim();
      continue;
    }

    flushList();
    if (!paragraph) paragraph = [];
    paragraph.push(line.trim());
  }

  flushAll();
  return blocks;
};

/** Plain text of inline tokens (used to compare a leading heading with the title). */
export const inlineToPlainText = (tokens) =>
  (tokens || [])
    .map((token) => (token.type === 'text' ? token.text : inlineToPlainText(token.children)))
    .join('');
